import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { useAIActivityStore } from '../stores/useAIActivityStore.js';

export interface ActivityRun {
  id: number;
  type: string;
  label: string;
  /** Stable i18n key emitted by server for runs whose label needs translation.
   *  E.g. 'scrape_run', 'bulk_analyze', 'bulk_draft', 'bulk_send', 'report_generate'.
   *  When present, frontend translates via t('activity.kind.<kind>').
   *  Virtual (client-side) runs leave this null. */
  kind?: string | null;
  step: string | null;
  startedAt: number;
  progress: { current: number; total: number; evaluated?: number } | null;
  pipelineId: number | null;
  linkTo: string;
}

export interface ActivityData {
  isActive: boolean;
  totalRuns: number;
  runs: ActivityRun[];
}

export function useActivity() {
  // Subscribe to virtual (client-side) AI generation runs
  const virtualRuns = useAIActivityStore(s => s.runs);

  const { data } = useQuery<ActivityData>({
    queryKey: ['activity'],
    queryFn: () => api.get<ActivityData>('/api/activity'),
    refetchInterval: (query) => {
      const d = query.state.data;
      // Keep polling fast while either server runs or virtual runs are active
      return (d?.isActive || useAIActivityStore.getState().runs.length > 0)
        ? 3_000
        : 10_000;
    },
    placeholderData: { isActive: false, totalRuns: 0, runs: [] },
  });

  const serverRuns = data?.runs ?? [];
  const allRuns = [...serverRuns, ...virtualRuns];

  return {
    isActive: allRuns.length > 0,
    totalRuns: allRuns.length,
    runs: allRuns,
  };
}
